/** @jsxImportSource @emotion/react */
import * as React from "react";
import { VenueTag } from "../../model/VenueTag";
import { Country } from "../../model/Country";
import * as _ from "lodash";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { FormRow } from "../common/hook-form";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";
import { TextField } from "../../components/forms/fields/TextField";
import { SelectField } from "../../components/forms/fields/SelectField";
import { SwitchField } from "../../components/forms/fields/SwitchField";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { PageFooter } from "../../components/page-skeleton/PageFooter";
import { ButtonPrimary } from "../../components/NewButtons";

interface OwnProps {
  countries: Country[];
  tags: VenueTag[] | null;
  isCountryFilterVisible: boolean;
  isNameFilterVisible: boolean;
  isIsActiveFilterVisible: boolean;
  isTagFilterVisible: boolean;
}

const gapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const VenueListFiltersForm: React.FC<OwnProps> = (props) => {
  const [t] = useTranslation();
  const {
    searchValue,
    setSearchValue,
    removeSearchValue,
    tag,
    setTag,
    removeTag,
    country,
    setCountry,
    removeCountry,
    venueOnlyVisible,
    setVenueOnlyVisible,
    removeVenueOnlyVisible,
  } = useSearchParamsManipulation();
  const tags = props.tags || [];

  const tagChoices = [
    {
      label: "All tags",
      value: "",
    },
    ..._.uniqWith(
      tags.map((x) => ({
        label: x.title,
        value: x.title,
      })),
      _.isEqual
    ),
  ];

  const mapCountryToChoices = (c: Country) => ({
    label: c.englishName,
    value: c.countryCode,
  });

  const countryChoices = [
    {
      label: "Any country",
      value: "",
    },
    ...props.countries
      .filter((x) => x.enabled === true)
      .map(mapCountryToChoices),
  ];

  return (
    <CardContainer>
      <PageHeader title={`${t("venue_list.filter.header")}`} />
      <CardBody>
        <div css={gapStyle}>
          <FormRow>
            {props.isNameFilterVisible && (
              <>
                {props.isNameFilterVisible && (
                  <TextField
                    value={searchValue || ""}
                    label={`${t("venue_list.filter.title.label")}`}
                    placeholder={`${t("venue_list.filter.title.placeholder")}`}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        removeSearchValue();
                      } else {
                        setSearchValue(e.target.value);
                      }
                    }}
                  />
                )}
              </>
            )}
          </FormRow>
          <FormRow>
            {(props.isTagFilterVisible || props.isCountryFilterVisible) && (
              <>
                {props.isTagFilterVisible && (
                  <SelectField
                    label={`${t("venue_list.filter.tag.label")}`}
                    onChange={(value) => {
                      const newValue = value as {
                        value: string;
                        label: string;
                      };

                      if (newValue.value === "") {
                        removeTag();
                      } else {
                        setTag(newValue.value);
                      }
                    }}
                    options={tagChoices}
                    value={tagChoices?.find((option) => option.value === tag)}
                  />
                )}
                {props.isCountryFilterVisible && (
                  <SelectField
                    label={`${t("venue_list.filter.country.label")}`}
                    onChange={(value) => {
                      const newValue = value as {
                        value: string;
                        label: string;
                      };
                      if (newValue.value === "") {
                        removeCountry();
                      } else {
                        setCountry([newValue.value]);
                      }
                    }}
                    options={countryChoices}
                    value={countryChoices?.find(
                      (option) => option.value === country
                    )}
                  />
                )}
              </>
            )}
          </FormRow>
          {props.isIsActiveFilterVisible && (
            <SwitchField
              checked={venueOnlyVisible === "true"}
              onChange={() => {
                if (venueOnlyVisible === "true") {
                  removeVenueOnlyVisible();
                } else {
                  setVenueOnlyVisible("true");
                }
              }}
              label="Only show visible restaurants"
            />
          )}
        </div>
      </CardBody>
      <PageFooter>
        <ButtonPrimary
          type="reset"
          onClick={() => {
            removeTag();
            removeCountry();
            removeVenueOnlyVisible();
            removeSearchValue();
          }}
        >
          {`${t("venue_list.filter.reset.label")}`}
        </ButtonPrimary>
      </PageFooter>
    </CardContainer>
  );
};

export default VenueListFiltersForm;
